import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, SlidersHorizontal, X, Search, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { EmptyState } from "@/components/EmptyState";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { format } from "@/lib/brand-tokens";
import { deals, brands, getBrandById } from "@/lib/mock-data";

// PUB-02 — Live deals showcase
export default function LiveDealsPage() {
  const active = useMemo(() => deals.filter((d) => d.status === "Published"), []);
  const [brandFilter, setBrandFilter] = useState([]);
  const [sort, setSort] = useState("rate");
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    // Normalize: lowercase + strip punctuation so "lowes" matches "Lowe's".
    const norm = (s) => (s || "").toLowerCase().replace(/[^a-z0-9 ]+/g, "");
    const nq = norm(q);
    let list = active.filter((d) => {
      if (brandFilter.length) {
        const dealBrands = d.brandIds || [d.brandId];
        if (!dealBrands.some((bid) => brandFilter.includes(bid))) return false;
      }
      if (nq) {
        const dealBrands = (d.brandIds || [d.brandId]).map(getBrandById).filter(Boolean);
        const haystackParts = [
          d.name,
          ...dealBrands.flatMap((b) => [b.name, ...(b.aliases || [])]),
        ];
        const haystack = norm(haystackParts.join(" "));
        if (!haystack.includes(nq)) return false;
      }
      return true;
    });
    if (sort === "rate") list.sort((a, b) => b.rate - a.rate);
    if (sort === "deadline") list.sort((a, b) => new Date(a.deadline) - new Date(b.deadline));
    if (sort === "newest") list.sort((a, b) => new Date(b.openedAt) - new Date(a.openedAt));
    return list;
  }, [active, brandFilter, sort, q]);

  const toggleBrand = (id) => setBrandFilter((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  const clearFilters = () => { setBrandFilter([]); setQ(""); };

  return (
    <div className="bg-surface">
      {/* Hero — soft mint wash matching home */}
      <section className="relative overflow-hidden bg-hero-rich border-b border-line">
        <div className="absolute inset-0 bg-grid-faint pointer-events-none opacity-50" />
        <div className="relative max-w-site mx-auto px-6 lg:px-12 pt-16 pb-14 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2.5 pl-2 pr-4 py-1.5 rounded-full bg-surface/70 backdrop-blur-sm border border-line shadow-xs mb-6">
              <span className="relative flex">
                <span className="size-2 rounded-full bg-emerald-500" />
                <span className="absolute inset-0 size-2 rounded-full bg-emerald-500 animate-pulse-ring" />
              </span>
              <span className="text-caption font-semibold text-ink uppercase tracking-wider">Live</span>
              <span className="size-1 rounded-full bg-line" />
              <span className="text-caption text-ink-muted tabular-nums">{active.length} active deals</span>
            </div>
            <h1 className="font-brand font-bold text-ink leading-[1.04] tracking-[-0.025em] text-[44px] sm:text-[60px] lg:text-[72px]">
              What we&rsquo;re buying{" "}
              <span className="relative inline-block text-brand-700">
                right now.
                <svg className="absolute -bottom-2 left-0 w-full" height="14" viewBox="0 0 300 14" fill="none" preserveAspectRatio="none">
                  <path d="M2 8 Q 80 2, 150 7 T 298 6" stroke="#5EEAD4" strokeWidth="3" strokeLinecap="round" fill="none" />
                </svg>
              </span>
            </h1>
            <p className="mt-7 text-lg text-ink-muted max-w-2xl leading-relaxed">
              Deals refresh every Monday. Rates are negotiated against pooled member supply &mdash;
              you&rsquo;ll never beat them solo. Apply to reserve and submit cards.
            </p>
          </div>

          {/* Right-side stats card — fills the previously-empty hero half. */}
          <div className="hidden lg:block lg:col-span-5">
            <div className="relative bg-surface border border-line rounded-3xl p-7 shadow-xl shadow-ink/[0.06]">
              <div className="flex items-center justify-between mb-5">
                <span className="eyebrow text-brand-700">This week</span>
                <span className="size-9 rounded-full bg-emerald-50 text-emerald-700 grid place-items-center"><Sparkles className="size-4" /></span>
              </div>
              <dl className="grid grid-cols-2 gap-x-6 gap-y-5">
                <div>
                  <dt className="text-caption text-ink-subtle">Live deals</dt>
                  <dd className="mt-1 font-brand text-[34px] font-bold text-ink leading-none tabular-nums">{active.length}</dd>
                </div>
                <div>
                  <dt className="text-caption text-ink-subtle">Brands</dt>
                  <dd className="mt-1 font-brand text-[34px] font-bold text-ink leading-none tabular-nums">{new Set(active.flatMap((d) => d.brandIds || [d.brandId])).size}</dd>
                </div>
                <div>
                  <dt className="text-caption text-ink-subtle">Best rate</dt>
                  <dd className="mt-1 font-brand text-[34px] font-bold text-brand-700 leading-none tabular-nums">{Math.max(...active.map((d) => d.rate || 0))}<span className="text-h2 text-ink-subtle">%</span></dd>
                </div>
                <div>
                  <dt className="text-caption text-ink-subtle">Closing soon</dt>
                  <dd className="mt-1 font-brand text-[34px] font-bold text-ink leading-none tabular-nums">{active.filter((d) => (new Date(d.deadline) - Date.now()) / 86400000 < 3).length}</dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </section>

      {/* Filter + cards */}
      <section className="max-w-site mx-auto px-6 lg:px-12 py-12">
        {/* Filter bar */}
        <div className="flex flex-wrap items-center gap-3 mb-8">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 size-4 text-ink-hint pointer-events-none" />
            <Input data-testid="deals-search" value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Search by deal name…"
                   className="pl-10 h-12 bg-surface-alt border-line text-body" />
          </div>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-[220px] h-12 bg-surface-alt border-line">
              <SlidersHorizontal className="size-3.5 mr-2 text-ink-muted" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="rate">Highest rate</SelectItem>
              <SelectItem value="deadline">Soonest deadline</SelectItem>
              <SelectItem value="newest">Newest</SelectItem>
            </SelectContent>
          </Select>
          {(brandFilter.length > 0 || q) && (
            <button onClick={clearFilters} className="ml-auto text-body-sm font-semibold text-ink-muted hover:text-ink underline-offset-4 hover:underline">
              Clear all filters
            </button>
          )}
        </div>

        {/* Brand chip row — counts deals that ACCEPT each brand (multi-brand aware) */}
        <div className="flex flex-wrap gap-2 mb-10">
          {brands.map((b) => {
            const isActive = brandFilter.includes(b.id);
            const count = active.filter((d) => (d.brandIds || [d.brandId]).includes(b.id)).length;
            if (count === 0) return null;
            return (
              <button
                key={b.id}
                onClick={() => toggleBrand(b.id)}
                data-testid={`brand-chip-${b.id}`}
                className={`px-4 py-2 rounded-full border text-body-sm font-semibold transition-all inline-flex items-center gap-2 ${
                  isActive
                    ? "bg-ink border-ink text-white"
                    : "bg-surface border-line text-ink-muted hover:border-ink hover:text-ink"
                }`}
              >
                <span>{b.name}</span>
                <span className={`tabular-nums ${isActive ? "text-white/70" : "text-ink-hint"}`}>{count}</span>
                {isActive && <X className="size-3" />}
              </button>
            );
          })}
        </div>

        {/* Deal cards */}
        {filtered.length === 0 ? (
          <EmptyState title="No deals match" description="Try clearing filters or signing up to be notified when new deals open." action="Apply to join" onAction={() => window.location.assign("/apply")} />
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((deal) => <PublicDealCard key={deal.id} deal={deal} />)}
          </div>
        )}
      </section>

      {/* Bottom CTA strip — encourages applying */}
      <section className="border-t border-line bg-surface-alt">
        <div className="max-w-site mx-auto px-6 lg:px-12 py-14 flex flex-wrap items-center justify-between gap-6">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-surface border border-line text-caption text-brand-700 mb-3">
              <Sparkles className="size-3 text-brand-700" />
              <span className="uppercase tracking-wider font-semibold">Now accepting applicants</span>
            </div>
            <h2 className="font-brand text-h1 text-ink font-bold tracking-[-0.02em]">Like what you see? Apply to reserve.</h2>
          </div>
          <Link to="/apply"
                className="group relative inline-flex items-center gap-3 h-14 pl-7 pr-3 rounded-full bg-brand-700 hover:bg-brand-800 text-white font-brand font-bold text-body shadow-[0_8px_24px_rgba(15,118,110,0.32)] hover:shadow-[0_12px_32px_rgba(15,118,110,0.42)] hover:-translate-y-0.5 transition-all">
            <span>Apply to join</span>
            <span className="size-9 rounded-full bg-white/15 grid place-items-center transition-transform group-hover:rotate-45 group-hover:bg-white/25">
              <ArrowUpRight className="size-4" strokeWidth={2.4} />
            </span>
          </Link>
        </div>
      </section>
    </div>
  );
}

function PublicDealCard({ deal }) {
  const brand = getBrandById(deal.brandId);
  const days = Math.max(0, Math.ceil((new Date(deal.deadline) - Date.now()) / 86_400_000));
  const fillPct = Math.round((deal.capUsed / deal.capTotal) * 100);
  const fillCopy = fillPct >= 80 ? "Filling fast" : fillPct >= 40 ? "Healthy availability" : "Plenty available";
  const monogram = brand?.name?.split(" ").map((w) => w[0]).slice(0, 2).join("") || "—";
  const urgent = fillPct >= 80 || days <= 2;
  // For multi-brand, surface a rate range so the headline matches what the
  // member sees on the detail page.
  const rates = (deal.brandConfigs || []).map(c => c.rate);
  const rateLabel = !deal.ratesUniform && rates.length > 1
    ? `${Math.min(...rates)}–${Math.max(...rates)}`
    : `${deal.rate}`;

  return (
    <Link
      to={`/deals/${deal.id}`}
      data-testid={`public-deal-card-${deal.id}`}
      className="group relative overflow-hidden bg-surface border border-line rounded-2xl transition-all hover:shadow-[0_24px_48px_-16px_rgba(15,23,42,0.18)] hover:-translate-y-1 hover:border-ink/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-700 focus-visible:ring-offset-2 block"
    >
      {/* Monogram watermark */}
      <div
        className="absolute -top-6 -right-2 font-brand font-black text-[140px] leading-none tracking-[-0.04em] text-ink/[0.04] select-none pointer-events-none transition-colors group-hover:text-brand-700/[0.06]"
        aria-hidden="true"
      >
        {monogram}
      </div>

      {/* Subtle teal corner glow on hover */}
      <div className="absolute -top-20 -left-20 w-48 h-48 rounded-full pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500"
           style={{ background: "radial-gradient(closest-side, rgba(15,118,110,0.10), transparent)" }} />

      <div className="relative p-7">
        <div className="flex items-center justify-between mb-7">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider font-semibold ${
            urgent
              ? "bg-ink text-white"
              : "bg-brand-50 text-brand-800 border border-brand-100"
          }`}>
            <span className={`size-1.5 rounded-full ${urgent ? "bg-brand-300 animate-pulse" : "bg-brand-700"}`} />
            {urgent ? "Filling fast" : "Open"}
          </span>
          <span className="text-caption text-ink-subtle tabular-nums">
            Closes in <span className="text-ink font-semibold">{days}d</span>
          </span>
        </div>

        <div>
          <h3 className="font-brand text-h2 text-ink font-bold leading-tight tracking-[-0.015em]">
            {deal.name}
          </h3>
          <div className="mt-1.5 text-caption text-ink-subtle truncate">
            {deal.isMultiBrand
              ? deal.brandIds.map((id) => getBrandById(id)?.name).filter(Boolean).join(" + ")
              : brand?.name}
          </div>
        </div>

        <div className="mt-7 pt-7 border-t border-line/70 flex items-end justify-between gap-4">
          <div>
            <div className="text-caption uppercase tracking-wider text-ink-hint">You receive</div>
            <div className="mt-1 flex items-baseline gap-1">
              <span className="font-brand text-[44px] font-bold text-brand-700 tabular-nums leading-none tracking-[-0.02em]">{rateLabel}</span>
              <span className="font-brand text-h2 font-bold text-brand-700 tabular-nums leading-none">%</span>
            </div>
            <div className="mt-1 text-caption text-ink-subtle">{deal.ratesUniform ? "of face value" : "rate range"}</div>
          </div>
          <div className="text-right">
            <div className="text-caption uppercase tracking-wider text-ink-hint">Face range</div>
            <div className="mt-1 font-brand text-h3 text-ink font-semibold tabular-nums leading-none">
              ${deal.minFace}<span className="text-ink-subtle">–</span>${deal.maxFace}
            </div>
          </div>
        </div>

        <div className="mt-7">
          <div className="flex items-center justify-between text-caption mb-2">
            <span className="text-ink-muted">{fillCopy}</span>
            <span className="text-ink-subtle tabular-nums">
              <span className="text-ink font-semibold">{format.currencyCompact(deal.capUsed)}</span>
              <span className="mx-1">/</span>
              {format.currencyCompact(deal.capTotal)}
              <span className="ml-2 text-ink-hint">·</span>
              <span className="ml-2 text-ink font-semibold">{fillPct}%</span>
            </span>
          </div>
          <div className="relative h-1.5 rounded-full bg-surface-alt overflow-hidden">
            <div
              className="absolute inset-y-0 left-0 bg-gradient-to-r from-brand-700 via-brand-600 to-brand-300 rounded-full transition-all duration-700"
              style={{ width: `${fillPct}%` }}
            />
          </div>
        </div>

        {/* Action affordance — the whole card is the link, this is just visual */}
        <span className="mt-7 inline-flex items-center gap-1.5 text-body-sm font-semibold text-ink group-hover:text-brand-700 transition-colors">
          See deal details
          <ArrowUpRight className="size-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.4} />
        </span>
      </div>
    </Link>
  );
}
